<template>
    <Modal @close="onClose">
        <SectionTitle :title="title"/>
        <form class="form">
            <div class="grid-container">
                <Input
                    v-model="form.address.name"
                    type="text"
                    :placeholder="config.PROFILE_ADDRESS_NAME_PLACEHOLDER"
                    :label="config.PROFILE_ADDRESS_NAME"
                />
                <Input
                    v-model="form.address.recipient"
                    type="text"
                    :label="config.PROFILE_ADDRESS_RECIPIENT"
                />
            </div>
            <AddressAutocomplete
                v-model="form.address.street"
                @select="onSelectAddress"
            />
            <Input
                v-model="form.address.complement"
                type="text"
                :placeholder="config.PROFILE_ADDRESS_COMPLEMENT_PLACEHOLDER"
                :label="config.PROFILE_ADDRESS_COMPLEMENT"
            />
            <div class="grid-container">
                <CountrySelect
                    v-model="form.address.country"
                    @select="onSelectCountry"
                />
                <StateSelect
                    v-model="form.address.state"
                    :country="form.address.country"
                    :disabled="!form.address.country"
                />
            </div>
            <div class="grid-container">
                <Input
                    v-model="form.address.city"
                    type="text"
                    :label="config.PROFILE_ADDRESS_CITY"
                />
                <Input
                    v-model="form.address.zipcode"
                    type="text"
                    :label="config.PROFILE_ADDRESS_ZIPCODE"
                />
            </div>
        </form>
        <section>
            <div v-if="error" class="error">
                {{ error.message }}
            </div>
            <SectionActions>
                <Button :color="config.COLOR_PRIMARY" :disabled="loading || !canSubmit" @click="onSave">
                    <Spinner v-if="loading" :color="config.COLOR_PRIMARY" class="spinner"/>
                    <span v-else>{{ config.PROFILE_SAVE_ADDRESS }}</span>
                </Button>
            </SectionActions>
        </section>
    </Modal>
</template>
<script>
import { TrackingConsumer, SolutionConsumer, UserConsumer } from '../../mixins';
import {
    SectionTitle, SectionActions, AddressAutocomplete, CountrySelect, StateSelect,
} from '../../components/shared';
import { Spinner } from '../lib';
import { getTextFromArray } from '../../utils';

export default {
    name: 'ModalAddress',
    components: {
        Spinner,
        SectionTitle,
        SectionActions,
        AddressAutocomplete,
        CountrySelect,
        StateSelect,
    },
    mixins: [TrackingConsumer, SolutionConsumer, UserConsumer],
    props: {
        address: {
            type: Object,
            default: () => {},
        },
    },
    data () {
        return {
            loading: false,
            error: null,
            form: {
                address: {},
            },
        };
    },
    computed: {
        title () {
            return this.form.address.id ? this.config.PROFILE_EDIT_ADDRESS_TITLE : this.config.PROFILE_CREATE_ADDRESS_TITLE;
        },
        canSubmit () {
            const {
                street, country, city, name, zipcode, recipient,
            } = this.form.address;
            return [street, country, city, name, zipcode, recipient].every((field) => typeof field === 'string' && field.trim());
        },
    },
    created () {
        const {
            id,
            street,
            country,
            state,
            city,
            zipcode,
            name,
            recipient = getTextFromArray([this.user.firstName, this.user.lastName]),
            complement,
        } = this.address;
        this.form.address = {
            id,
            street,
            country,
            state,
            city,
            zipcode,
            name,
            recipient,
            complement,
        };
    },
    methods: {
        onClose () {
            this.$emit('close');
        },
        async onSave () {
            this.loading = true;
            this.error = null;

            try {
                if (this.form.address.id) {
                    await this.updateAddress(this.form.address);
                } else {
                    await this.createAddress(this.form.address);
                }

                await this.updateUser();
                this.onClose();
            } catch (err) {
                this.error = err;
            } finally {
                this.loading = false;
            }
        },
        onSelectAddress (option) {
            ['country', 'state', 'city', 'street', 'zipcode'].forEach((field) => {
                this.form.address[field] = option[field];
            });
        },
        onSelectCountry (countryCode) {
            this.form.address.country = countryCode;
            this.form.address.state = null;
        },
    },
};
</script>
<style lang="scss" scoped>
.form {

  > * {
    margin-bottom: 10px;
  }

  .grid-container {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
}
section {
  .error {
    text-align: center;
    font-size: 12px;
    margin-bottom: 10px;
    color: red;
  }

  .spinner {
    width: 20px;
    height: 20px;
  }

  .spinner + span {
    margin-right: 20px;
  }
}
</style>
